import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { usePermissions } from "@/contexts/PermissionsContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DeploymentDrillDown from "./DeploymentDrillDown";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Package,
  LogOut,
  RefreshCw,
  Loader2,
  Check,
  X,
  Clock,
  AlertCircle,
  Monitor,
  Cloud,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  BarChart3,
  Rocket,
  Users,
  Server,
  Plus,
  Minus,
  UserPlus,
  Search,
  Activity,
  Shield,
  Layers,
  ChevronDown,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Timer,
  Flame,
  Undo2,
  PauseCircle,
  Wrench,
} from "lucide-react";

// Colours for the stages after UAT. A rollout's pipeline can have 1-10 of
// them (deployment pipelines), so the palette cycles rather than being keyed
// by phase number. Labels come from the phase itself (see phaseLabel).
const PHASE_PALETTE = [
  { bg: "rgba(56,189,248,0.2)", border: "#38bdf8", text: "#7dd3fc" },
  { bg: "rgba(192,132,252,0.2)", border: "#c084fc", text: "#d8b4fe" },
  { bg: "rgba(163,230,53,0.2)", border: "#a3e635", text: "#bef264" },
  { bg: "rgba(251,191,36,0.2)", border: "#fbbf24", text: "#fcd34d" },
  { bg: "rgba(244,114,182,0.2)", border: "#f472b6", text: "#f9a8d4" },
];
const phaseColor = (n) => PHASE_PALETTE[(Math.max(1, Number(n) || 1) - 1) % PHASE_PALETTE.length];

// Rollouts started before deployment pipelines have no phase_count and
// exactly three phases. Only used when the data carries no count at all.
const LEGACY_PHASE_COUNT = 3;

// A stage that targets an existing Entra group as-is (no percentage sample).
const isNamedGroupPhase = (p) => !!p && (p.predefined === true || p.stage_type === "group");

// "Pilot", "Phase 2": the stage's name alone.
const phaseName = (p, n) => {
  if (isNamedGroupPhase(p)) return p?.name || p?.group_name || `Phase ${n}`;
  return p?.name || `Phase ${n}`;
};

// "Pilot", "Phase 2 (50%)": the name plus the percentage when it has one.
const phaseLabel = (p, n) => {
  const name = phaseName(p, n);
  if (isNamedGroupPhase(p)) return name;
  return p?.percentage != null ? `${name} (${p.percentage}%)` : name;
};

// Normalise a phased deployment document's phases.phase1..N into an array,
// driven by phase_count (falling back to the highest phaseN key, then 3).
const phaseListFromDoc = (doc) => {
  const phasesObj = doc?.phases || {};
  const keyed = Object.keys(phasesObj)
    .map((k) => /^phase(\d+)$/.exec(k))
    .filter(Boolean)
    .map((m) => Number(m[1]));
  const count = Number(doc?.phase_count) > 0
    ? Number(doc.phase_count)
    : (keyed.length ? Math.max(...keyed) : LEGACY_PHASE_COUNT);
  return Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    const entry = phasesObj[`phase${n}`] || {};
    return { ...entry, phase: n, name: phaseName(entry, n), label: phaseLabel(entry, n) };
  });
};

// Per-row (GET /deployments) equivalents: the row carries phase_count and
// phase_names, or nulls when there is no phased document yet.
const rowPhaseCount = (d) => {
  if (Number(d?.phase_count) > 0) return Number(d.phase_count);
  if (Array.isArray(d?.phase_names) && d.phase_names.length) return d.phase_names.length;
  return LEGACY_PHASE_COUNT;
};
const rowPhaseName = (d, n) => (Array.isArray(d?.phase_names) && d.phase_names[n - 1]) || `Phase ${n}`;
const isLiveCompleted = (status) => status === "completed" || status === "complete";
const usedPreUat = (d) => d?.deployment_stage === "pre_uat" || !!d?.pre_uat_stage_name;

const HEALTH_STATUS = {
  in_progress: { color: "#006AF2", bg: "rgba(0,106,242,0.12)", icon: Activity, label: "Active" },
  completed: { color: "#22c55e", bg: "rgba(34,197,94,0.12)", icon: CheckCircle2, label: "Completed" },
  failed: { color: "#FC5442", bg: "rgba(252,84,66,0.12)", icon: XCircle, label: "Failed" },
  pending: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", icon: Clock, label: "Pending" },
};

function HealthDashboard({ data, loading, onRefresh }) {
  const summary = data?.summary || {};
  const deployments = data?.deployments || [];

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-white/50" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deployment-health-section">
      {/* Summary Row */}
      <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Layers, label: "Total", value: summary.total_deployments || 0, color: "#006AF2", bg: "rgba(0,106,242,0.12)" },
          { icon: Activity, label: "Active", value: summary.active || 0, color: "#8B5CF6", bg: "rgba(139,92,246,0.12)" },
          { icon: CheckCircle2, label: "Completed", value: summary.completed || 0, color: "#22c55e", bg: "rgba(34,197,94,0.12)" },
          { icon: XCircle, label: "Failed", value: summary.failed || 0, color: "#FC5442", bg: "rgba(252,84,66,0.12)" },
        ].map(({ icon: Icon, label, value, color, bg }) => (
          <Card key={label} className="glass-card border-[rgba(229,239,250,0.15)]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-white/70 uppercase tracking-wider">{label}</p>
                  <p className="text-3xl font-bold text-white mt-1">{value}</p>
                </div>
                <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: bg }}>
                  <Icon className="h-6 w-6" style={{ color }} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overall Device Coverage */}
      {summary.total_deployments > 0 && (
        <Card className="glass-card border-[rgba(229,239,250,0.15)]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Monitor className="h-4 w-4 text-white/70" />
                <span className="text-sm font-medium text-white/90">Overall Device Coverage</span>
              </div>
              <span className="text-sm font-bold text-white">
                {summary.total_devices_installed || 0} / {summary.total_devices_targeted || 0} devices
              </span>
            </div>
            <Progress
              value={summary.total_devices_targeted > 0 ? ((summary.total_devices_installed || 0) / summary.total_devices_targeted) * 100 : 0}
              className="h-2.5 bg-white/10"
            />
          </CardContent>
        </Card>
      )}

      {/* Deployment Cards */}
      {deployments.length === 0 ? (
        <Card className="glass-card border-[rgba(229,239,250,0.15)]">
          <CardContent className="p-12 text-center">
            <Shield className="h-12 w-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-white/70 font-medium text-lg">No Phased Deployments</h3>
            <p className="text-white/50 text-sm mt-2">
              Start a phased "Go Live" deployment from the Discover page to see health metrics here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {deployments.map((dep) => {
            const statusCfg = HEALTH_STATUS[dep.health] || HEALTH_STATUS.pending;
            const StatusIcon = statusCfg.icon;
            const targeted = dep.total_devices_targeted || 0;
            const installed = dep.total_devices_installed || 0;
            const st = dep.intune_status || {};

            return (
              <Card key={dep.deployment_id || dep.app_name} className="glass-card border-[rgba(229,239,250,0.15)] hover:border-[rgba(229,239,250,0.3)] transition-all duration-200" data-testid={`health-${dep.app_name}`}>
                <CardContent className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {dep.icon ? (
                        <img
                          src={dep.icon}
                          alt=""
                          className="h-10 w-10 rounded-lg bg-white/10 p-1 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                        />
                      ) : null}
                      <div className={`h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-purple-500/30 items-center justify-center text-white font-bold text-base ${dep.icon ? 'hidden' : 'flex'}`}>
                        {(dep.app_name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-sm">{dep.app_name}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          {dep.version && <span className="text-[11px] text-white/70">v{dep.version}</span>}
                          {dep.publisher && <span className="text-[11px] text-white/50">{dep.publisher}</span>}
                        </div>
                      </div>
                    </div>
                    <Badge className="text-[11px] px-2.5 py-0.5 font-medium border-0" style={{ background: statusCfg.bg, color: statusCfg.color }}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusCfg.label}
                    </Badge>
                  </div>

                  {/* Phase Progress Bars */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    {(dep.phases || []).map((phase) => {
                      const cfg = phaseColor(phase.phase);
                      const isCurrent = phase.status === "active";
                      const isCompleted = phase.status === "complete" || phase.status === "completed";
                      const isPending = phase.status === "pending";
                      const fillPct = phase.install_progress != null ? phase.install_progress : 0;
                      // Name from the pipeline; a percentage only when the
                      // stage has one (named Entra groups don't).
                      const label = phase.name
                        ? (phase.percentage != null && phase.stage_type !== "group" ? `${phase.name} - ${phase.percentage}%` : phase.name)
                        : (phase.percentage != null ? `Phase ${phase.phase} - ${phase.percentage}%` : `Phase ${phase.phase}`);

                      return (
                        <div key={phase.phase} className="flex-1 min-w-[120px]" data-testid={`health-phase-${dep.app_name}-${phase.phase}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span
                              className="text-[11px] font-semibold uppercase tracking-wider truncate"
                              style={{ color: isPending ? "rgba(255,255,255,0.45)" : cfg.text }}
                              title={phase.stage_type === "group" && phase.group_name ? `${label} — Entra group ${phase.group_name}` : label}
                            >
                              {label}
                            </span>
                            {isCurrent && (
                              <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: cfg.border }} />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: cfg.border }} />
                              </span>
                            )}
                            {isCompleted && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                            )}
                          </div>
                          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${fillPct}%`, background: isPending ? "rgba(255,255,255,0.08)" : cfg.border, opacity: isPending ? 0.3 : 1 }} />
                          </div>
                          <div className="flex justify-between mt-1">
                            {isCompleted ? (
                              <span className="text-[10px] text-emerald-400 font-medium">Complete</span>
                            ) : isCurrent ? (
                              <span className="text-[10px] text-white/70">{installed}/{phase.device_count ?? "—"} installed</span>
                            ) : (
                              <span className="text-[10px] text-white/40">Pending</span>
                            )}
                            {isCurrent && <span className="text-[10px] text-white/60">{fillPct}%</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bottom: Intune Status + Device Count */}
                  <div className="flex items-end justify-between pt-3 border-t border-white/10">
                    <div className="flex-1">
                      <p className="text-[10px] text-white/60 uppercase tracking-wider mb-1.5">Install Status</p>
                      <div className="flex h-1.5 rounded-full overflow-hidden bg-white/10 mb-1.5">
                        {(st.installed || 0) > 0 && <div className="bg-emerald-400" style={{ width: `${Math.round(((st.installed || 0) / Math.max(targeted, 1)) * 100)}%` }} />}
                        {(st.failed || 0) > 0 && <div className="bg-[#FC5442]" style={{ width: `${Math.round(((st.failed || 0) / Math.max(targeted, 1)) * 100)}%` }} />}
                        {(st.deferred || 0) > 0 && <div className="bg-sky-400" style={{ width: `${Math.round(((st.deferred || 0) / Math.max(targeted, 1)) * 100)}%` }} />}
                        {(st.pending || 0) > 0 && <div className="bg-amber-400" style={{ width: `${Math.round(((st.pending || 0) / Math.max(targeted, 1)) * 100)}%` }} />}
                      </div>
                      <div className="flex gap-3 text-[10px] flex-wrap">
                        <span className="text-emerald-400">{st.installed || 0} installed</span>
                        <span className="text-[#FC5442]">{st.failed || 0} failed</span>
                        {(st.deferred || 0) > 0 && (
                          <span className="text-sky-300" title="User chose Defer — Intune retries on the next sync">
                            {st.deferred} deferred
                          </span>
                        )}
                        <span className="text-amber-400">{st.pending || 0} pending</span>
                        <span className="text-white/50">{st.not_received || 0} not received</span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-[10px] text-white/60 uppercase tracking-wider">Devices</p>
                      <p className="text-lg font-bold text-white">{installed}<span className="text-white/50 text-xs font-normal">/{targeted}</span></p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DeploymentsPage() {
  const { user, logout, getAccessToken } = useAuth();
  const { canManageTenant, canDeployPackages } = usePermissions();

  // State
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_deployments: 0,
    total_devices: 0,
    installed: 0,
    pending: 0,
    failed: 0,
    // User deferrals (Win32 1602 / 0x80070642) — neither installed nor
    // failed. See fetch_intune_device_statuses on the backend.
    deferred: 0
  });
  
  // Detail modal
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Phased deployment state
  const [showPhasedDeployModal, setShowPhasedDeployModal] = useState(false);
  const [phasedDeployTarget, setPhasedDeployTarget] = useState(null);
  // The stages the family's next rollout will use (GET
  // /deployment-pipeline/resolve), shown in the Go LIVE modal before the
  // rollout starts. { loading, data, error }.
  const [resolvedPipeline, setResolvedPipeline] = useState({ loading: false, data: null, error: null });
  const [deployingPhase, setDeployingPhase] = useState(false);
  const [phaseStatuses, setPhaseStatuses] = useState({});

  // Pre-UAT -> UAT promotion (row action, confirmed first).
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promoting, setPromoting] = useState(false);

  // Rollback confirmation modal state. Rolls the deployment back by
  // exactly one stage (stage N -> stage N-1, first stage -> Test).
  // Multi-tenant safe: the backend resolves the tenant from the
  // X-ALICE-Tenant-ID header (added by the shared axios interceptor)
  // via TenantMiddleware, so the Intune reassignment lands in the
  // tenant the user is currently viewing.
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  // Proceed-to-next-phase confirmation modal (row-level action).
  const [showProceedConfirm, setShowProceedConfirm] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  // Test members edit modal
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [editingDeployment, setEditingDeployment] = useState(null);
  const [editMembers, setEditMembers] = useState([""]);
  const [savingMembers, setSavingMembers] = useState(false);
  
  // Status refresh state
  const [refreshingStatus, setRefreshingStatus] = useState({});
  const [rebuildingPhase, setRebuildingPhase] = useState({});
  const [hasLiveData, setHasLiveData] = useState(false);
  // Diagnostic returned by the backend when live Intune status can't be
  // fetched for the selected tenant (e.g. admin consent required).
  const [graphStatus, setGraphStatus] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("deployments");

  // Health dashboard state
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Table UX: search, sort, expand-row, drill-down
  const [searchQuery, setSearchQuery] = useState("");
  // Sort keys: app_name | version | deployment_stage | time_in_stage_hours | deployed_at
  const [sort, setSort] = useState({ key: "deployed_at", dir: "desc" });
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [drillDownId, setDrillDownId] = useState(null);

  // Grouping tab — "Action required" (failure rate > 10% with >=3 attempts) /
  // Live / Test / All. Defaults to `action_required` when the group has any
  // rows so admins land on what needs them first.
  const [group, setGroup] = useState("all");
  const [groupInitialized, setGroupInitialized] = useState(false);

  // Fetch deployments with auth token for real Intune status
  const fetchDeployments = useCallback(async () => {
    setLoading(true);
    try {
      // Get access token for real Intune status
      const accessToken = await getAccessToken();
      
      const headers = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      
      // Backend paginates (default limit=25). Ask for a larger page so the
      // table keeps showing every deployment without adding pager UI.
      const response = await axios.get(`${API}/deployments`, {
        headers,
        params: { limit: 200 },
        // Fail fast instead of spinning forever if the backend is slow to
        // reach Azure/Graph — the catch below clears the spinner and toasts.
        timeout: 30000,
      });
      setDeployments(response.data.deployments || []);
      setStats(response.data.stats || {});
      setHasLiveData(response.data.has_live_data || false);
      setGraphStatus(response.data.graph_status || null);
    } catch (error) {
      console.error("Failed to fetch deployments:", error);
      toast.error("Failed to load deployments");
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  // Fetch deployment health data
  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/deployment-health`, { headers });
      setHealthData(response.data);
    } catch (error) {
      console.error("Failed to fetch deployment health:", error);
    } finally {
      setHealthLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  // Fetch health data when switching to that tab, and auto-refresh
  useEffect(() => {
    if (activeTab === "health") {
      fetchHealth();
      const interval = setInterval(fetchHealth, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab, fetchHealth]);

  // Start live deployment
  const handleLiveDeployment = async (deployment) => {
    // Open new phased deployment modal
    setPhasedDeployTarget(deployment);
    setShowPhasedDeployModal(true);

    // Check for existing phased deployment by app name and/or intune_app_id
    let found = false;
    try {
      let url = `${API}/discover/go-live-by-app/${encodeURIComponent(deployment.app_name)}`;
      if (deployment.intune_app_id) {
        url += `?intune_app_id=${encodeURIComponent(deployment.intune_app_id)}`;
      }
      const response = await axios.get(url);
      if (response.data.found && response.data.deployment) {
        const dep = response.data.deployment;
        found = true;
        // Every stage after UAT from the document (phase_count of them;
        // three for rollouts started before deployment pipelines).
        const phases = phaseListFromDoc(dep);
        setPhaseStatuses({
          phases,
          phase_count: phases.length,
          pipeline: dep.pipeline || null,
          current_phase: dep.current_phase || 1,
          deployment_id: dep.deployment_id,
          status: dep.status,
          total_devices: dep.total_devices,
        });
      } else {
        setPhaseStatuses({ phases: [], current_phase: 0 });
      }
    } catch (error) {
      console.error("Error fetching phase status:", error);
      setPhaseStatuses({ phases: [], current_phase: 0 });
    }

    // No rollout yet: show the stages the family's next rollout will use.
    // The family name is the one executePhasedDeployment sends as
    // display_name_normalized.
    if (!found) {
      setResolvedPipeline({ loading: true, data: null, error: null });
      try {
        const res = await axios.get(`${API}/deployment-pipeline/resolve`, {
          params: { family_name: deployment.app_name },
        });
        setResolvedPipeline({ loading: false, data: res.data || null, error: null });
      } catch (error) {
        console.error("Error resolving deployment pipeline:", error);
        const detail = error.response?.data?.detail;
        setResolvedPipeline({
          loading: false,
          data: null,
          error: typeof detail === "string" ? detail : "Could not load the deployment pipeline for this app.",
        });
      }
    } else {
      setResolvedPipeline({ loading: false, data: null, error: null });
    }
  };

  // Toast text for a proceed response: "Pilot initiated", "Phase 2 (50%)
  // initiated! 120 devices." next_phase_name already carries the percentage.
  const proceedToastText = (data) => {
    const name = data.next_phase_name
      || (data.percentage != null ? `Phase ${data.next_phase} (${data.percentage}%)` : `Phase ${data.next_phase}`);
    const devices = data.device_count != null ? ` ${data.device_count} devices.` : "";
    return `${name} initiated!${devices}`;
  };

  // Proceed to next phase
  const proceedToNextPhase = async () => {
    const deploymentId = phaseStatuses.deployment_id;
    if (!deploymentId) {
      toast.error("No active deployment found");
      return;
    }

    setDeployingPhase(true);
    
    try {
      const accessToken = await getAccessToken();
      
      if (!accessToken) {
        toast.error("Authentication required. Please sign in.");
        setDeployingPhase(false);
        return;
      }

      const response = await axios.post(
        `${API}/discover/go-live-proceed/${deploymentId}`,
        {},
        { 
          headers: { 
            "Authorization": `Bearer ${accessToken}`
          } 
        }
      );

      toast.success(proceedToastText(response.data), { duration: 8000 });

      // Refresh phase statuses
      setTimeout(() => handleLiveDeployment(phasedDeployTarget), 2000);
      
    } catch (error) {
      console.error("Error proceeding to next phase:", error);
      const errorMsg = error.response?.data?.detail || "Failed to proceed to next phase";
      toast.error(errorMsg);
    } finally {
      setDeployingPhase(false);
    }
  };

  // Roll back the current deployment by exactly one stage:
  //   stage N     -> stage N-1
  //   first stage -> Test  (the app is re-assigned to the package's test
  //                         group with intent "available")
  // The backend endpoint /discover/rollback-phase/{id} resolves the
  // tenant from the middleware-set X-ALICE-Tenant-ID header, so this
  // works correctly for tenant-switched users.
  const rollbackPhase = async () => {
    const deploymentId = phaseStatuses.deployment_id;
    const fromPhase = phaseStatuses.current_phase || 0;
    const count = phaseStatuses.phase_count || phaseStatuses.phases?.length || LEGACY_PHASE_COUNT;

    if (!deploymentId) {
      toast.error("No active deployment found");
      return;
    }
    if (fromPhase < 1 || fromPhase > count) {
      toast.error("Deployment is not in a rollback-eligible state");
      return;
    }

    setRollingBack(true);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        toast.error("Authentication required. Please sign in.");
        setRollingBack(false);
        return;
      }

      const response = await axios.post(
        `${API}/discover/rollback-phase/${deploymentId}`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const data = response.data;
      const fromName = phaseStatuses.phases?.find((p) => p.phase === data.from_phase)?.name
        || `Phase ${data.from_phase}`;
      toast.success(
        `Rollback initiated: ${fromName} -> ${data.to}. Intune reassignment in progress.`,
        { duration: 8000 }
      );

      setShowRollbackConfirm(false);
      // Give the background task a moment to reassign, then refresh
      setTimeout(() => handleLiveDeployment(phasedDeployTarget), 2500);
    } catch (error) {
      console.error("Error rolling back phase:", error);
      const errorMsg = error.response?.data?.detail || "Failed to roll back phase";
      toast.error(errorMsg);
    } finally {
      setRollingBack(false);
    }
  };

  // Trigger the rollback confirmation dialog directly from a
  // deployment row (Actions column). Fetches the current phased-
  // deployment state for this specific package VERSION (using
  // ``intune_app_id`` as the authoritative per-version key -- see
  // 2026-07-02 fix) so the dialog can display "Phase N -> Phase N-1"
  // and hand the right deployment_id to ``rollbackPhase``.
  const handleRollbackFromRow = async (deployment) => {
    setPhasedDeployTarget(deployment);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        toast.error("Authentication required. Please sign in.");
        return;
      }
      let url = `${API}/discover/go-live-by-app/${encodeURIComponent(deployment.app_name)}`;
      if (deployment.intune_app_id) {
        url += `?intune_app_id=${encodeURIComponent(deployment.intune_app_id)}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.data.found || !response.data.deployment) {
        toast.error(
          "No phased deployment found for this app version. It may not be Live yet."
        );
        return;
      }

      const dep = response.data.deployment;
      const currentPhase = dep.current_phase || 0;
      const phases = phaseListFromDoc(dep);
      if (currentPhase < 1 || currentPhase > phases.length) {
        toast.error(
          `Deployment is not in a rollback-eligible state (current stage: ${currentPhase}).`
        );
        return;
      }

      // Hydrate the state the confirm dialog reads from: current_phase,
      // deployment_id and the phases (for the stage names it shows).
      setPhaseStatuses({
        phases,
        phase_count: phases.length,
        current_phase: currentPhase,
        deployment_id: dep.deployment_id,
        status: dep.status,
        app_name: dep.app_name,
        version: dep.version,
      });
      setShowRollbackConfirm(true);
    } catch (e) {
      console.error("Error loading deployment for rollback:", e);
      toast.error(
        e.response?.data?.detail || "Failed to load deployment status for rollback"
      );
    }
  };

  // Trigger the proceed-to-next-phase confirmation dialog directly from
  // a deployment row (Actions column). Same hydration pattern as
  // ``handleRollbackFromRow``: fetch the phased deployment for THIS
  // version so the dialog shows "Phase N -> Phase N+1" and the confirm
  // handler has the right deployment_id.
  const handleProceedFromRow = async (deployment) => {
    setPhasedDeployTarget(deployment);
    try {
      const accessToken = await getAccessToken();
      const headers = {};
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      let url = `${API}/discover/go-live-by-app/${encodeURIComponent(deployment.app_name)}`;
      if (deployment.intune_app_id) {
        url += `?intune_app_id=${encodeURIComponent(deployment.intune_app_id)}`;
      }
      const response = await axios.get(url, { headers });

      if (!response.data.found || !response.data.deployment) {
        toast.error(
          "No phased deployment found for this app version. It may not be Live yet."
        );
        return;
      }

      const dep = response.data.deployment;
      const currentPhase = dep.current_phase || 0;
      const phases = phaseListFromDoc(dep);
      if (currentPhase < 1 || currentPhase >= phases.length || isLiveCompleted(dep.status)) {
        toast.error(
          `Deployment cannot proceed to a next stage (current stage: ${phases[currentPhase - 1]?.name || currentPhase}).`
        );
        return;
      }

      setPhaseStatuses({
        phases,
        phase_count: phases.length,
        current_phase: currentPhase,
        deployment_id: dep.deployment_id,
        status: dep.status,
        app_name: dep.app_name,
        version: dep.version,
      });
      setShowProceedConfirm(true);
    } catch (e) {
      console.error("Error loading deployment for phase proceed:", e);
      toast.error(
        e.response?.data?.detail || "Failed to load deployment status"
      );
    }
  };

  // Confirmed proceed from the row-level dialog. Same endpoint as the
  // Discover page / phased modal, but refreshes the deployments table
  // instead of re-opening the modal.
  const confirmProceedPhase = async () => {
    const deploymentId = phaseStatuses.deployment_id;
    if (!deploymentId) {
      toast.error("No active deployment found");
      return;
    }

    setDeployingPhase(true);
    try {
      const accessToken = await getAccessToken();
      const headers = {};
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

      const response = await axios.post(
        `${API}/discover/go-live-proceed/${deploymentId}`,
        {},
        { headers }
      );

      toast.success(proceedToastText(response.data), { duration: 8000 });

      setShowProceedConfirm(false);
      // Give the backend a moment to assign, then refresh the table
      setTimeout(() => fetchDeployments(), 2500);
    } catch (error) {
      console.error("Error proceeding to next phase:", error);
      const errorMsg = error.response?.data?.detail || "Failed to proceed to next phase";
      toast.error(errorMsg);
    } finally {
      setDeployingPhase(false);
    }
  };



  // Execute phased deployment - Uses detected apps like DiscoverPage (no CSV needed)
  const executePhasedDeployment = async () => {
    setDeployingPhase(true);
    
    try {
      // Get access token for Graph API
      const accessToken = await getAccessToken();
      
      if (!accessToken) {
        toast.error("Authentication required. Please sign in.");
        setDeployingPhase(false);
        return;
      }

      // Call the same endpoint as DiscoverPage - auto-detects devices from detected_apps
      const response = await axios.post(
        `${API}/discover/go-live-phased`,
        {
          display_name_normalized: phasedDeployTarget.app_name,
          app_name: phasedDeployTarget.app_name,
          intune_app_id: phasedDeployTarget.intune_app_id,
          version: phasedDeployTarget.version || "1.0"
        },
        { 
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`
          } 
        }
      );

      const data = response.data;
      // The first stage after UAT may be a percentage sample or a named
      // Entra group; word the toast from what the pipeline actually holds.
      const first = data.phases?.phase1 || {};
      const second = data.phases?.phase2;
      const firstLabel = phaseLabel(first, 1);
      const devicesText = first.device_count != null
        ? ` ${first.device_count}${data.total_devices != null ? ` of ${data.total_devices}` : ""} devices.`
        : isNamedGroupPhase(first) && first.group_name
          ? ` Assigning to Entra group '${first.group_name}'.`
          : "";
      const nextText = second ? ` Monitor success rate, then proceed to ${phaseName(second, 2)}.` : "";
      toast.success(`${firstLabel} deployed!${devicesText}${nextText}`, { duration: 10000 });
      
      // Store deployment ID for phase progression
      localStorage.setItem(`go_live_${phasedDeployTarget.app_name}`, data.deployment_id);
      
      setShowPhasedDeployModal(false);
      fetchDeployments();
      
    } catch (error) {
      console.error("Error initiating Go Live:", error);
      const errorMsg = error.response?.data?.detail || "Failed to initiate Go Live";
      toast.error(errorMsg);
    } finally {
      setDeployingPhase(false);
    }
  };

  // Move a Pre-UAT package to UAT. The backend creates the test group and
  // emails the testers in the background, so refresh now and again shortly
  // after; a failure shows up as the row's uat_promotion_error. Needs the
  // bearer token: the background task uses it for Graph.
  const confirmPromoteToUat = async () => {
    if (!promoteTarget) return;
    setPromoting(true);
    try {
      const accessToken = await getAccessToken();
      const headers = {};
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      const response = await axios.post(
        `${API}/packages/${promoteTarget.id}/promote-to-uat`,
        {},
        { headers }
      );
      toast.success(response.data?.message || "Moving to UAT", { duration: 8000 });
      setPromoteTarget(null);
      fetchDeployments();
      setTimeout(() => fetchDeployments(), 5000);
    } catch (error) {
      console.error("Error moving package to UAT:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to move to UAT");
    } finally {
      setPromoting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      deployed: { icon: CheckCircle2, className: "text-green-300 border-2 border-green-500 bg-green-500/20 font-semibold" },
      deploying: { icon: Loader2, className: "text-blue-300 border-2 border-blue-500 bg-blue-500/20 font-semibold", spin: true },
      failed: { icon: XCircle, className: "text-red-300 border-2 border-red-500 bg-red-500/20 font-semibold" },
      pending: { icon: Clock, className: "text-yellow-300 border-2 border-yellow-500 bg-yellow-500/20 font-semibold" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant="outline" className={config.className}>
        <Icon className={`h-3 w-3 mr-1 ${config.spin ? "animate-spin" : ""}`} />
        {status}
      </Badge>
    );
  };
  
  // Stage badge for a /deployments row. Completion is the rollout's status
  // ("completed"), never "current phase is 3": a pipeline can have 1-10
  // stages after UAT. Stage names come from the row's phase_names.
  const getStageBadge = (d) => {
    const stage = d?.deployment_stage;
    const currentPhase = d?.current_phase;
    const liveStatus = d?.live_deployment_status;

    if (stage === "pre_uat") {
      return (
        <Badge
          variant="outline"
          className="text-sky-300 border-2 border-sky-500 bg-sky-500/20 font-semibold"
          title={d.pre_uat_group_name ? `Available to Entra group ${d.pre_uat_group_name}` : undefined}
          data-testid={`pre-uat-badge-${d.id}`}
        >
          <Users className="h-3 w-3 mr-1" />
          {d.pre_uat_stage_name || "Pre-UAT"}
        </Badge>
      );
    }

    // If we have a current_phase > 0, it's definitely a LIVE deployment (phased rollout)
    if (currentPhase && currentPhase > 0) {
      const isComplete = isLiveCompleted(liveStatus);
      return (
        <Badge className={`${isComplete ? 'bg-purple-600' : 'bg-green-600'} text-white`}>
          <Rocket className="h-3 w-3 mr-1" />
          {isComplete ? 'LIVE' : `LIVE - ${rowPhaseName(d, currentPhase)}`}
        </Badge>
      );
    }

    // Check deployment_stage
    if (stage === "live") {
      // Phase from the live status string ("phase4_active").
      const m = /^phase(\d+)_active$/.exec(liveStatus || "");
      const phase = m ? Number(m[1]) : null;
      const isComplete = isLiveCompleted(liveStatus);

      return (
        <Badge className={`${isComplete ? 'bg-purple-600' : 'bg-green-600'} text-white`}>
          <Rocket className="h-3 w-3 mr-1" />
          {isComplete ? 'LIVE' : phase ? `LIVE - ${rowPhaseName(d, phase)}` : 'LIVE'}
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-yellow-300 border-2 border-yellow-500 bg-yellow-500/20 font-semibold">
        <Users className="h-3 w-3 mr-1" />
        TEST
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Open manage members modal
  const handleManageMembers = async (deployment) => {
    setEditingDeployment(deployment);
    
    // Fetch current members
    try {
      const response = await axios.get(`${API}/packages/${deployment.id}/test-members`);
      const members = response.data.test_members || [];
      setEditMembers(members.length > 0 ? members : [""]);
    } catch (error) {
      setEditMembers([""]);
    }
    
    setShowMembersModal(true);
  };

  // Add member input
  const addMemberInput = () => {
    if (editMembers.length < 5) {
      setEditMembers([...editMembers, ""]);
    }
  };

  // Update member value
  const updateMember = (index, value) => {
    const updated = [...editMembers];
    updated[index] = value;
    setEditMembers(updated);
  };

  // Remove member input
  const removeMember = (index) => {
    if (editMembers.length > 1) {
      setEditMembers(editMembers.filter((_, i) => i !== index));
    }
  };

  // Save updated members
  const handleSaveMembers = async () => {
    const validMembers = editMembers.filter(m => m.trim());
    if (validMembers.length === 0) {
      toast.error("Please add at least one test member");
      return;
    }

    setSavingMembers(true);
    try {
      // Get access token for Azure AD group updates
      const accessToken = await getAccessToken();
      
      const headers = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      
      const response = await axios.put(
        `${API}/packages/${editingDeployment.id}/test-members`, 
        { members: validMembers },
        { headers }
      );
      
      if (response.data.azure_group_updated) {
        toast.success("Test members updated in both database and Azure AD group");
      } else if (response.data.azure_errors && response.data.azure_errors.length > 0) {
        toast.warning(`Database updated. Azure AD issues: ${response.data.azure_errors.join(", ")}`);
      } else if (!accessToken) {
        toast.success("Test members updated in database (login required for Azure AD sync)");
      } else {
        toast.success("Test members updated in database");
      }
      setShowMembersModal(false);
      fetchDeployments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update test members");
    } finally {
      setSavingMembers(false);
    }
  };

  // Recreate a missing phase record from the app's Intune assignments so a
  // LIVE row with no phase gets its Next phase / Rollback controls back.
  const handleRebuildPhase = async (deployment) => {
    setRebuildingPhase(prev => ({ ...prev, [deployment.id]: true }));
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        toast.error("Authentication required. Please sign in.");
        return;
      }
      const response = await axios.post(
        `${API}/deployments/${deployment.id}/rebuild-phase`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toast.success(response.data.message || "Phase rebuilt from Intune");
      await fetchDeployments();
    } catch (error) {
      console.error("Failed to rebuild phase:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to rebuild phase from Intune");
    } finally {
      setRebuildingPhase(prev => ({ ...prev, [deployment.id]: false }));
    }
  };

  // Refresh deployment status from Intune
  const handleRefreshStatus = async (deploymentId) => {
    setRefreshingStatus(prev => ({ ...prev, [deploymentId]: true }));
    try {
      const accessToken = await getAccessToken();
      
      if (!accessToken) {
        toast.error("Login required to refresh status from Intune");
        return;
      }
      
      const response = await axios.post(
        `${API}/deployments/${deploymentId}/refresh-status`,
        {},
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );
      
      // Handle different response scenarios
      if (response.data.warning) {
        // App has no assignments
        toast.warning(response.data.warning);
      } else {
        const stats = response.data.stats;
        toast.success(
          `Status refreshed: ${stats.installed}/${stats.total} installed, ` +
          `${stats.pending} pending, ${stats.failed} failed` +
          (stats.deferred ? `, ${stats.deferred} deferred` : "")
        );
      }
      
      // Log token method for debugging
      if (response.data.token_method) {
        console.log(`Token method used: ${response.data.token_method}`);
      }
      
      // Refresh the deployments list
      await fetchDeployments();
    } catch (error) {
      console.error("Failed to refresh status:", error);
      
      // Handle structured error responses
      const errorDetail = error.response?.data?.detail;
      
      if (typeof errorDetail === 'object') {
        // New structured error format
        const message = errorDetail.message || "Failed to refresh status";
        const code = errorDetail.code || "unknown";
        const requestId = errorDetail.request_id;
        
        // Show user-friendly message with details
        if (code === "Authorization_RequestDenied" || error.response?.status === 403) {
          toast.error("Permission denied. Admin consent may be required for DeviceManagementApps permissions.", {
            duration: 8000
          });
        } else if (error.response?.status === 404) {
          toast.error("App not found in Intune. It may have been deleted.");
        } else {
          toast.error(`${message}${requestId ? ` (Request ID: ${requestId})` : ""}`);
        }
        
        // Log full details for debugging
        console.error("Graph API Error Details:", errorDetail);
        if (errorDetail.troubleshooting) {
          console.log("Troubleshooting tips:", errorDetail.troubleshooting);
        }
      } else {
        // Legacy string error format
        toast.error(errorDetail || "Failed to refresh status from Intune");
      }
    } finally {
      setRefreshingStatus(prev => ({ ...prev, [deploymentId]: false }));
    }
  };
  
  // Get status source badge
  const getStatusSourceBadge = (statusSource, cacheAge, statusError) => {
    if (statusSource === "intune_live") {
      return (
        <Badge variant="outline" className="text-green-300 border-2 border-green-500 bg-green-500/20 font-semibold text-xs">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Live from Intune
        </Badge>
      );
    }
    if (statusSource === "intune_cached") {
      const minutes = Math.floor(cacheAge / 60);
      return (
        <Badge variant="outline" className="text-blue-300 border-2 border-blue-500 bg-blue-500/20 font-semibold text-xs">
          <Clock className="h-3 w-3 mr-1" />
          Cached ({minutes}m ago)
        </Badge>
      );
    }
    if (statusSource === "intune_cached_expired") {
      return (
        <Badge variant="outline" className="text-yellow-300 border-2 border-yellow-500 bg-yellow-500/20 font-semibold text-xs">
          <AlertCircle className="h-3 w-3 mr-1" />
          Stale Cache
        </Badge>
      );
    }
    if (statusSource === "error") {
      const errorCode = statusError?.code || "Unknown";
      return (
        <Badge variant="outline" className="text-red-300 border-2 border-red-500 bg-red-500/20 font-semibold text-xs" title={statusError?.message}>
          <XCircle className="h-3 w-3 mr-1" />
          Error: {errorCode}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-gray-300 border-2 border-gray-500 bg-gray-500/20 font-semibold text-xs">
        <AlertCircle className="h-3 w-3 mr-1" />
        No Intune Data
      </Badge>
    );
  };

  const calculateProgress = (deployment) => {
    const total = deployment.device_stats?.total || 0;
    const installed = deployment.device_stats?.installed || 0;
    if (total === 0) return 0;
    return Math.round((installed / total) * 100);
  };

  // -------------------------------------------------------------------------
  // Grouping — classifies each row into: action_required | live | test.
  // `action_required` means >=3 device install attempts AND failure rate > 10%.
  // User deferrals are categorised as "deferred" by the backend and are
  // therefore in NEITHER `installed` nor `failed` — a deployment that lots
  // of users deferred is not "action required", it is just waiting.
  // Stuck deployments stay out of this group on purpose (they live in the
  // Needs Attention banner + Test tab for the user to triage).
  //
  // For live/test classification we mirror `getStageBadge`: a row is LIVE
  // iff `current_phase > 0` OR deployment_stage is "live" / "live-phase*".
  // Everything else (null, "", "test", "deployed_test", "pre-test",
  // "awaiting_approval", etc.) is TEST — otherwise PACE Suite / PeaZip
  // rows with a TEST badge leak into the Live tab.
  const isLiveStage = (d) => {
    if ((d.current_phase || 0) > 0) return true;
    const s = (d.deployment_stage || "").toLowerCase();
    return s === "live" || s.startsWith("live-") || s.startsWith("live_");
  };

  const classifyGroup = (d) => {
    const stats = d.device_stats || {};
    const installed = stats.installed || 0;
    const failed = stats.failed || 0;
    const attempts = installed + failed;
    if (attempts >= 3 && (failed / attempts) > 0.10) return "action_required";
    return isLiveStage(d) ? "live" : "test";
  };

  const deploymentsWithGroup = useMemo(
    () => (deployments || []).map((d) => ({ ...d, _group: classifyGroup(d) })),
    [deployments],
  );

  const groupCounts = useMemo(() => {
    const c = { action_required: 0, live: 0, test: 0, all: deploymentsWithGroup.length };
    deploymentsWithGroup.forEach((d) => { c[d._group] = (c[d._group] || 0) + 1; });
    return c;
  }, [deploymentsWithGroup]);

  // On first data load, jump to "Action required" if that group is non-empty.
  useEffect(() => {
    if (groupInitialized || !deploymentsWithGroup.length) return;
    setGroup(groupCounts.action_required > 0 ? "action_required" : "all");
    setGroupInitialized(true);
  }, [deploymentsWithGroup.length, groupCounts.action_required, groupInitialized]);

  // -------------------------------------------------------------------------
  // Derived table state: search + sort + group filter applied client-side.
  // -------------------------------------------------------------------------
  const visibleDeployments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = deploymentsWithGroup;
    if (group !== "all") {
      list = list.filter((d) => d._group === group);
    }
    if (q) {
      list = list.filter((d) => {
        const hay = [
          d.app_name, d.version, d.publisher, d.intune_app_id, d.target_group,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = sort.key;
    const cmp = (a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      if (key === "deployed_at" || key === "stage_entered_at") {
        return (new Date(av || 0).getTime() - new Date(bv || 0).getTime()) * dir;
      }
      if (key === "time_in_stage_hours") {
        return ((av ?? 0) - (bv ?? 0)) * dir;
      }
      return String(av || "").localeCompare(String(bv || ""), undefined, { sensitivity: "base" }) * dir;
    };
    return [...list].sort(cmp);
  }, [deploymentsWithGroup, group, searchQuery, sort]);

  const stuckDeployments = useMemo(
    () => (deployments || []).filter((d) => d.is_stuck),
    [deployments],
  );

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const SortIcon = ({ column }) => {
    if (sort.key !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sort.dir === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Go LIVE modal: the resolved pipeline (before a rollout exists)...
  const resolvedStages = resolvedPipeline.data?.stages || [];
  const resolvedPostUatStages = resolvedStages.filter((s) => s.type === "group" || s.type === "percentage");
  const resolvedStageLabel = (s, i) => {
    if (s.type === "percentage") {
      const name = s.name || `Phase ${i + 1}`;
      return s.percentage != null ? `${name} (${s.percentage}%)` : name;
    }
    return s.name || s.group_name || `Stage ${i + 1}`;
  };
  const resolvedStageChipLabel = (s) => {
    if (s.type === "pre_uat") return s.name || "Pre-UAT";
    if (s.type === "uat") return s.name || "UAT";
    return resolvedStageLabel(s, resolvedPostUatStages.indexOf(s));
  };
  // ...and the stages either side of the current one (once it exists),
  // for the Rollback / Proceed buttons and their confirm dialogs.
  const currentPhaseNum = phaseStatuses.current_phase || 0;
  const currentPhaseEntry = phaseStatuses.phases?.find((p) => p.phase === currentPhaseNum) || null;
  const nextPhaseEntry = phaseStatuses.phases?.find((p) => p.phase === currentPhaseNum + 1) || null;
  const rollbackTargetName = currentPhaseNum <= 1
    ? "Test"
    : (phaseStatuses.phases?.find((p) => p.phase === currentPhaseNum - 1)?.name || `Phase ${currentPhaseNum - 1}`);

  // Compact "stage" visual, one segment per pipeline stage:
  // [Pre-UAT, if this package used it] -> Test/UAT -> each stage after UAT.
  const PhaseProgress = ({ deployment: d }) => {
    const stage = d?.deployment_stage;
    const phase = Number(d?.current_phase || 0);
    const completed = isLiveCompleted(d?.live_deployment_status);
    const isPreUat = stage === "pre_uat";
    const isTest = stage === "test";
    const count = rowPhaseCount(d);
    const steps = [];
    if (usedPreUat(d)) {
      steps.push({
        key: "pre_uat",
        label: d.pre_uat_stage_name || "Pre-UAT",
        active: true,
        done: !isPreUat,
      });
    }
    steps.push({ key: "uat", label: "Test / UAT", active: isTest || phase >= 1, done: phase >= 1 });
    for (let n = 1; n <= count; n++) {
      steps.push({
        key: `phase${n}`,
        label: rowPhaseName(d, n),
        active: phase >= n,
        done: phase > n || (phase >= n && completed),
      });
    }
    const title = isPreUat
      ? `In ${d.pre_uat_stage_name || "Pre-UAT"}`
      : isTest
        ? "In testing"
        : completed
          ? "Live — rollout complete"
          : phase ? `Live — ${rowPhaseName(d, phase)}` : "Live";
    // Up to 12 segments (Pre-UAT, UAT, 10 stages): narrow them when many.
    const segWidth = steps.length > 6 ? "w-3" : "w-6";
    return (
      <div className="flex items-center gap-1 flex-wrap" title={title} data-testid={`phase-progress-${d?.id}`}>
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div
              title={s.label}
              className={`${segWidth} h-2 rounded-sm ${
                s.done ? "bg-emerald-400"
                : s.active ? "bg-cyan-400"
                : "bg-white/10"
              }`}
            />
            {i < steps.length - 1 && <span className="text-white/20 text-[10px]">›</span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const TimeInStage = ({ hours, isStuck, stage }) => {
    if (hours === null || hours === undefined) {
      return <span className="text-white/40 text-xs">—</span>;
    }
    const display = hours < 24
      ? `${Math.round(hours)}h`
      : `${Math.round((hours / 24) * 10) / 10}d`;
    let cls = "text-white/70";
    if (isStuck) cls = "text-rose-300";
    else if ((stage === "test" || stage === "pre_uat") && hours >= 72) cls = "text-amber-300";
    else if (stage === "live" && hours >= 24 * 10) cls = "text-amber-300";
    return (
      <span className={`inline-flex items-center gap-1 text-xs ${cls}`}>
        <Timer className="w-3 h-3" />
        {display}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Main Content */}
      <main className="p-6 w-full" data-testid="deployments-main">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Deployments</h1>
            <p className="text-white/90 mt-1">Monitor application deployment status across devices</p>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={fetchDeployments}
            className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC]"
            data-testid="refresh-deployments"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Live Intune status unavailable — surfaces admin-consent / token issues per tenant */}
        {graphStatus && graphStatus.available === false && graphStatus.reason && graphStatus.reason !== "no_tenant" && (
          <div
            className="mb-6 rounded-xl border border-amber-400/30 bg-amber-400/10 backdrop-blur-[5px] p-4"
            data-testid="graph-status-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-400/20 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-300" />
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold">Live Intune status unavailable</div>
                <p className="text-sm text-white/80 mt-1" data-testid="graph-status-message">
                  {graphStatus.message ||
                    "Live device install status can't be fetched for this tenant right now. The deployment list below still reflects the latest known data."}
                </p>
                {/* Restoring runs from Settings > Enterprise App Permissions,
                    which brings the admin back into ALICE and re-checks. A
                    direct /adminconsent link landed on the admin site. */}
                {graphStatus.restore_permissions && (
                  canManageTenant ? (
                    <Link
                      to="/settings?section=integrations"
                      className="inline-flex items-center gap-2 mt-3 text-sm font-medium text-[#3C02CC] bg-[#BBFF71] hover:brightness-95 px-3 py-1.5 rounded-lg transition-[filter]"
                      data-testid="graph-status-restore-link"
                    >
                      Restore permissions in Settings
                    </Link>
                  ) : (
                    <p className="text-xs text-white/60 mt-2" data-testid="graph-status-restore-hint">
                      Ask a tenant admin in ALICE to restore permissions from Settings › Integrations.
                    </p>
                  )
                )}
              </div>
            </div>
          </div>
        )}


        {/* Needs Attention — stuck deployments escalation banner */}
        {stuckDeployments.length > 0 && activeTab === "deployments" && (
          <div
            className="mb-6 rounded-xl border border-rose-500/30 bg-rose-500/10 backdrop-blur-[5px] p-4"
            data-testid="needs-attention-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-rose-500/20 flex-shrink-0">
                <Flame className="w-5 h-5 text-rose-300" />
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold flex items-center gap-2">
                  Needs attention
                  <span className="text-xs text-rose-200 bg-rose-500/20 px-2 py-0.5 rounded-full font-normal">
                    {stuckDeployments.length} stuck
                  </span>
                </div>
                <p className="text-sm text-white/80 mt-1">
                  These deployments have exceeded the expected time in their current
                  stage (&gt; 1 week in test, &gt; 2 weeks in a live phase). Review
                  progress or escalate as needed.
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="needs-attention-chips">
                  {stuckDeployments.slice(0, 8).map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setDrillDownId(d.id)}
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 hover:bg-rose-500/25 text-xs text-white"
                      data-testid={`needs-attention-${d.id}`}
                    >
                      <span className="font-medium">{d.app_name}</span>
                      <span className="text-rose-200">
                        {d.time_in_stage_hours && d.time_in_stage_hours >= 24
                          ? `${Math.round(d.time_in_stage_hours / 24)}d`
                          : `${Math.round(d.time_in_stage_hours || 0)}h`}
                        {" · "}
                        {d.deployment_stage === "test"
                          ? "in test"
                          : d.deployment_stage === "pre_uat"
                            ? `in ${d.pre_uat_stage_name || "Pre-UAT"}`
                            : d.current_phase ? rowPhaseName(d, d.current_phase) : "live"}
                      </span>
                    </button>
                  ))}
                  {stuckDeployments.length > 8 && (
                    <span className="text-xs text-white/60 self-center">
                      + {stuckDeployments.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex gap-1 p-1 rounded-xl bg-[rgba(229,239,250,0.1)] mb-6 w-fit" data-testid="deployments-tabs">
          <button
            onClick={() => setActiveTab("deployments")}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeTab === "deployments"
                ? "bg-[#006AF2] text-white shadow-lg"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
            data-testid="tab-deployments"
          >
            <Rocket className="h-4 w-4 inline-block mr-2 -mt-0.5" />
            Deployments
          </button>
          <button
            onClick={() => setActiveTab("health")}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeTab === "health"
                ? "bg-[#006AF2] text-white shadow-lg"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
            data-testid="tab-health"
          >
            <Activity className="h-4 w-4 inline-block mr-2 -mt-0.5" />
            Deploy Health
          </button>
        </div>

        {activeTab === "deployments" && (
        <>
        {/* Stats Cards - Glass Effect */}
        <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-deployments">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Total</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-400/20">
                <Cloud className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.total_deployments}</div>
            <div className="text-sm text-white/90">Deployments</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-devices">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Target</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-400/20">
                <Monitor className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.total_devices}</div>
            <div className="text-sm text-white/90">Devices</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-installed">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Installed</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-400/20">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.installed}</div>
            <div className="text-sm text-white/90">Successful</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-pending">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Pending</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-yellow-400/20">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.pending}</div>
            <div className="text-sm text-white/90">In Progress</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-deferred" title="Devices where the user chose Defer. Intune retries on the next sync — these are not failures.">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Deferred</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-sky-400/20">
                <PauseCircle className="w-5 h-5 text-sky-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.deferred || 0}</div>
            <div className="text-sm text-white/90">By User</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-failed">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Failed</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-400/20">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.failed}</div>
            <div className="text-sm text-white/90">Errors</div>
          </div>
        </div>

        {/* Group tab bar — Action required / Live / Test / All */}
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="deployments-group-tabs">
          {[
            { key: "action_required", label: "Action required", icon: Flame,
              active: "bg-rose-500/20 text-rose-200 border-rose-400/40",
              idle: "text-rose-300/90" },
            { key: "live",             label: "Live",            icon: Rocket,
              active: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
              idle: "text-emerald-300/90" },
            { key: "test",             label: "Test",            icon: Activity,
              active: "bg-cyan-500/20 text-cyan-200 border-cyan-400/40",
              idle: "text-cyan-300/90" },
            { key: "all",              label: "All",             icon: Layers,
              active: "bg-white/10 text-white border-white/20",
              idle: "text-white/80" },
          ].map(({ key, label, icon: Icon, active, idle }) => {
            const count = groupCounts[key] ?? 0;
            const isActive = group === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setGroup(key)}
                data-testid={`group-tab-${key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                  isActive
                    ? `${active}`
                    : `bg-white/[0.03] ${idle} border-transparent hover:bg-white/[0.06]`
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
                <span
                  className={`ml-1 inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-white/10 text-white/70"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search bar for the table */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search deployments by name, publisher, group…"
              className="pl-8 bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.1)] text-white placeholder:text-white/60"
              data-testid="deployments-search-input"
            />
          </div>
          {searchQuery && (
            <span className="text-xs text-white/60">
              {visibleDeployments.length} of {deployments.length}
            </span>
          )}
        </div>

        {/* Deployments Table */}
        <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          ) : deployments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Cloud className="h-12 w-12 mb-4 text-[#E5EFFA]/30" />
              <p className="text-white/90">No deployments yet</p>
              <p className="text-sm text-white/70">Deploy a package from the Packages page to see it here</p>
              <Link to="/packages">
                <Button className="mt-4 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
                  Go to Packages
                </Button>
              </Link>
            </div>
          ) : visibleDeployments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="deployments-search-empty">
              <Search className="h-10 w-10 mb-3 text-white/40" />
              <p className="text-white/90">
                {searchQuery
                  ? `No deployments match "${searchQuery}"${group !== "all" ? ` in ${group.replace("_", " ")}` : ""}`
                  : group === "action_required"
                    ? "Nothing requires action — failure rate is below 10% across all deployments."
                    : group === "test"
                      ? "No test-stage deployments."
                      : group === "live"
                        ? "No live deployments."
                        : "No deployments yet"}
              </p>
              {(searchQuery || group !== "all") && (
                <div className="mt-3 flex gap-2">
                  {searchQuery && (
                    <Button
                      variant="outline"
                      onClick={() => setSearchQuery("")}
                      data-testid="deployments-search-clear"
                    >
                      Clear search
                    </Button>
                  )}
                  {group !== "all" && (
                    <Button
                      variant="outline"
                      onClick={() => setGroup("all")}
                      data-testid="deployments-group-show-all"
                    >
                      Show all deployments
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[rgba(229,239,250,0.1)] hover:bg-white/[0.04]">
                  <TableHead className="text-white w-8"></TableHead>
                  <TableHead
                    className="text-white cursor-pointer select-none"
                    onClick={() => toggleSort("app_name")}
                    data-testid="sort-app-name"
                  >
                    <span className="inline-flex items-center">Application<SortIcon column="app_name" /></span>
                  </TableHead>
                  <TableHead
                    className="text-white cursor-pointer select-none"
                    onClick={() => toggleSort("version")}
                    data-testid="sort-version"
                  >
                    <span className="inline-flex items-center">Version<SortIcon column="version" /></span>
                  </TableHead>
                  <TableHead className="text-white">Stage</TableHead>
                  <TableHead
                    className="text-white cursor-pointer select-none"
                    onClick={() => toggleSort("time_in_stage_hours")}
                    data-testid="sort-time-in-stage"
                  >
                    <span className="inline-flex items-center">In stage<SortIcon column="time_in_stage_hours" /></span>
                  </TableHead>
                  <TableHead className="text-white">Target Group</TableHead>
                  <TableHead className="text-white">Device Progress</TableHead>
                  <TableHead
                    className="text-white cursor-pointer select-none"
                    onClick={() => toggleSort("deployed_at")}
                    data-testid="sort-deployed-at"
                  >
                    <span className="inline-flex items-center">Deployed<SortIcon column="deployed_at" /></span>
                  </TableHead>
                  <TableHead className="text-right text-white">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleDeployments.map((deployment) => (
                  <React.Fragment key={deployment.id}>
                  <TableRow
                    data-testid={`deployment-row-${deployment.id}`}
                    className={`border-[rgba(229,239,250,0.1)] hover:bg-white/[0.04] ${
                      deployment.is_stuck ? "bg-rose-500/5" : ""
                    }`}
                  >
                    <TableCell className="p-1 pl-3">
                      <button
                        type="button"
                        onClick={() => setExpandedRowId(
                          expandedRowId === deployment.id ? null : deployment.id
                        )}
                        className="text-white/60 hover:text-white"
                        data-testid={`expand-row-${deployment.id}`}
                        aria-label="Expand"
                      >
                        {expandedRowId === deployment.id
                          ? <ChevronDown className="w-4 h-4" />
                          : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">
                          {deployment.app_name}
                          {deployment.is_stuck && (
                            <Flame className="inline-block w-3.5 h-3.5 ml-2 text-rose-400" title="Stuck in current stage" />
                          )}
                        </p>
                        <p className="text-xs text-white/80 font-mono">{deployment.intune_app_id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant="secondary" className="font-mono bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white w-fit">{deployment.version}</Badge>
                        {deployment.lifecycle === "retired" && (
                          <Badge
                            variant="outline"
                            className="text-slate-300 border-slate-500 bg-slate-500/20 w-fit text-xs"
                            title={`Retired from Intune${deployment.retired_at ? ` on ${String(deployment.retired_at).slice(0, 10)}` : ""}. Figures are the last ones before retirement.`}
                            data-testid={`deployment-retired-badge-${deployment.id}`}
                          >
                            Retired
                          </Badge>
                        )}
                        {deployment.silent_mode === true && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 w-fit font-mono text-emerald-300 border-emerald-500/60 bg-emerald-500/10"
                            title={
                              `Deployed silently — Intune install command:\n${deployment.silent_mode_install_command || 'Invoke-AppDeployToolkit.exe -DeploymentType Install -DeployMode Silent'}\n\n(PSADT prompts suppressed on end-user devices)` +
                              (deployment.silent_mode_source === 'intune_live' ? '\n\nResolved live from Intune.' : '')
                            }
                            data-testid={`silent-mode-badge-${deployment.id}`}
                          >
                            🔇 Silent
                          </Badge>
                        )}
                        {deployment.silent_mode === false && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 w-fit font-mono text-amber-300 border-amber-500/60 bg-amber-500/10"
                            title={
                              `Deployed interactively — Intune install command:\n${deployment.silent_mode_install_command || 'Invoke-AppDeployToolkit.exe -DeploymentType Install'}\n\n(PSADT prompts visible to end-user)` +
                              (deployment.silent_mode_source === 'intune_live' ? '\n\nResolved live from Intune.' : '')
                            }
                            data-testid={`silent-mode-badge-${deployment.id}`}
                          >
                            👁 Interactive
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1.5">
                        {getStageBadge(deployment)}
                        <PhaseProgress deployment={deployment} />
                        {deployment.uat_promotion_error && (
                          <p
                            className="text-[11px] text-red-300 max-w-[220px] break-words"
                            title={deployment.uat_promotion_error}
                            data-testid={`uat-promotion-error-${deployment.id}`}
                          >
                            <AlertTriangle className="inline-block h-3 w-3 mr-1 -mt-0.5" />
                            Move to UAT failed: {deployment.uat_promotion_error}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <TimeInStage
                        hours={deployment.time_in_stage_hours}
                        isStuck={deployment.is_stuck}
                        stage={deployment.deployment_stage}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium text-white truncate max-w-[150px]" title={deployment.target_group}>
                          {deployment.target_group}
                        </p>
                        {deployment.deployment_stage === "test" && deployment.test_group_members?.length > 0 && (
                          <p className="text-xs text-white/80">
                            {deployment.test_group_members.length} test members
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="w-44">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-green-600">{deployment.device_stats?.installed || 0} installed</span>
                          <span className="text-white/80">{deployment.device_stats?.total || 0} total</span>
                        </div>
                        <Progress value={calculateProgress(deployment)} className="h-2" />
                        <div className="flex items-center gap-2 mt-1 text-xs">
                          {deployment.device_stats?.pending > 0 && (
                            <span className="text-yellow-600">{deployment.device_stats.pending} pending</span>
                          )}
                          {deployment.device_stats?.deferred > 0 && (
                            <span
                              className="text-sky-400"
                              title="User chose Defer — Intune retries on the next sync"
                            >
                              {deployment.device_stats.deferred} deferred
                            </span>
                          )}
                          {deployment.device_stats?.failed > 0 && (
                            <span className="text-red-600">{deployment.device_stats.failed} failed</span>
                          )}
                        </div>
                        <div className="mt-1">
                          {getStatusSourceBadge(deployment.status_source, deployment.cache_age_seconds, deployment.status_error)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-white">{formatDate(deployment.deployed_at)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {deployment.deployment_stage === "test" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRefreshStatus(deployment.id)}
                              disabled={refreshingStatus[deployment.id]}
                              data-testid={`refresh-status-btn-${deployment.id}`}
                              title="Refresh status from Intune"
                              className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                            >
                              <RefreshCw className={`h-4 w-4 ${refreshingStatus[deployment.id] ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleManageMembers(deployment)}
                              data-testid={`manage-members-btn-${deployment.id}`}
                              title="Manage test group members"
                              className="bg-blue-500 text-white border-blue-600 hover:bg-blue-600 font-semibold"
                            >
                              <UserPlus className="h-4 w-4 mr-1" />
                              Members
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleLiveDeployment(deployment)}
                              data-testid={`live-deploy-btn-${deployment.id}`}
                            >
                              <Rocket className="h-3 w-3 mr-1" />
                              Go Live
                            </Button>
                          </>
                        )}
                        {/* Pre-UAT: the app is Available to an existing Entra
                            group with no testers yet. Moving to UAT is always
                            a person's decision (the scheduler never does it).
                            Hidden without deploy_packages; the backend checks
                            the same permission. */}
                        {deployment.deployment_stage === "pre_uat" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRefreshStatus(deployment.id)}
                              disabled={refreshingStatus[deployment.id]}
                              data-testid={`refresh-status-btn-${deployment.id}`}
                              title="Refresh status from Intune"
                              className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                            >
                              <RefreshCw className={`h-4 w-4 ${refreshingStatus[deployment.id] ? 'animate-spin' : ''}`} />
                            </Button>
                            {canDeployPackages && (
                              <Button
                                size="sm"
                                className="bg-sky-600 hover:bg-sky-700 text-white font-semibold"
                                onClick={() => setPromoteTarget(deployment)}
                                data-testid={`promote-uat-btn-${deployment.id}`}
                                title="Create the test group, assign the app and email the testers"
                              >
                                <Users className="h-4 w-4 mr-1" />
                                Move to UAT
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDrillDownId(deployment.id)}
                          data-testid={`details-btn-${deployment.id}`}
                          className="bg-purple-500 text-white border-purple-600 hover:bg-purple-600 font-semibold"
                        >
                          <BarChart3 className="h-4 w-4 mr-1" />
                          Details
                        </Button>
                        {/* Rollback control for LIVE deployments, at any stage
                            of the rollout's pipeline (1..phase_count).
                            Each package VERSION has its own rollback lifecycle
                            (see 2026-07-02 per-version fix) -- this button
                            operates on THIS row's ``intune_app_id`` only. */}
                        {deployment.deployment_stage !== "test" &&
                         deployment.deployment_stage !== "pre_uat" &&
                         deployment.current_phase >= 1 &&
                         deployment.current_phase <= rowPhaseCount(deployment) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRollbackFromRow(deployment)}
                            data-testid={`rollback-row-btn-${deployment.id}`}
                            title={
                              deployment.current_phase === 1
                                ? "Rollback this version to the Test group"
                                : `Rollback this version to ${rowPhaseName(deployment, deployment.current_phase - 1)}`
                            }
                            className="border-amber-500 text-amber-400 hover:bg-amber-500/15 hover:text-amber-300 font-semibold"
                          >
                            <Undo2 className="h-4 w-4 mr-1" />
                            {deployment.current_phase === 1
                              ? "Rollback to Test"
                              : `Rollback to ${rowPhaseName(deployment, deployment.current_phase - 1)}`}
                          </Button>
                        )}
                        {/* Proceed control while the rollout has a stage left
                            -- advances THIS row's version to the next stage
                            (same endpoint as the Discover page). */}
                        {deployment.deployment_stage !== "test" &&
                         deployment.deployment_stage !== "pre_uat" &&
                         deployment.current_phase >= 1 &&
                         deployment.current_phase < rowPhaseCount(deployment) &&
                         !isLiveCompleted(deployment.live_deployment_status) && (
                          <Button
                            size="sm"
                            onClick={() => handleProceedFromRow(deployment)}
                            data-testid={`proceed-row-btn-${deployment.id}`}
                            title={`Advance this version to ${rowPhaseName(deployment, deployment.current_phase + 1)}`}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                          >
                            <Rocket className="h-4 w-4 mr-1" />
                            Go to {rowPhaseName(deployment, deployment.current_phase + 1)}
                          </Button>
                        )}
                        {/* LIVE with no phase record (e.g. wiped by an old
                            package delete): rebuild it from the app's Intune
                            assignments so the phase controls come back. */}
                        {deployment.deployment_stage === "live" &&
                         !deployment.current_phase && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRebuildPhase(deployment)}
                            disabled={!!rebuildingPhase[deployment.id]}
                            data-testid={`rebuild-phase-btn-${deployment.id}`}
                            title="Work out this version's phase from its Intune assignments"
                            className="border-sky-400 text-sky-300 hover:bg-sky-500/15 hover:text-sky-200 font-semibold"
                          >
                            {rebuildingPhase[deployment.id]
                              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              : <Wrench className="h-4 w-4 mr-1" />}
                            Rebuild phase
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {expandedRowId === deployment.id && (
                    <TableRow
                      className="border-[rgba(229,239,250,0.1)] bg-white/[0.02]"
                      data-testid={`expand-content-${deployment.id}`}
                    >
                      <TableCell colSpan={9} className="py-4">
                        <div className="px-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                            <div>
                              <div className="text-[11px] uppercase tracking-wider text-white/60">Stage entered</div>
                              <div className="text-white/90">{formatDate(deployment.stage_entered_at) || "—"}</div>
                            </div>
                            <div>
                              <div className="text-[11px] uppercase tracking-wider text-white/60">Current stage</div>
                              <div className="text-white/90">
                                {deployment.deployment_stage === "test"
                                  ? "In testing"
                                  : deployment.deployment_stage === "pre_uat"
                                    ? `${deployment.pre_uat_stage_name || "Pre-UAT"}${deployment.pre_uat_group_name ? ` (${deployment.pre_uat_group_name})` : ""}`
                                    : deployment.current_phase
                                      ? `${rowPhaseName(deployment, deployment.current_phase)} (stage ${deployment.current_phase} of ${rowPhaseCount(deployment)})`
                                      : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-[11px] uppercase tracking-wider text-white/60">Status source</div>
                              <div className="text-white/90">
                                {deployment.status_source || "—"}
                                {typeof deployment.cache_age_seconds === "number" && (
                                  <span className="text-white/50 text-xs ml-2">
                                    ({Math.round(deployment.cache_age_seconds / 60)}m old)
                                  </span>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-[11px] uppercase tracking-wider text-white/60">Intune App ID</div>
                              <div className="text-white/90 font-mono text-xs break-all">
                                {deployment.intune_app_id || "—"}
                              </div>
                            </div>
                          </div>
                          {deployment.per_device_status?.length > 0 ? (
                            <div className="overflow-x-auto border border-white/10 rounded-lg">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-[11px] uppercase text-white/60 bg-white/5">
                                    <th className="px-3 py-2 font-medium">Device</th>
                                    <th className="px-3 py-2 font-medium">User</th>
                                    <th className="px-3 py-2 font-medium">State</th>
                                    <th className="px-3 py-2 font-medium">Last sync</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {deployment.per_device_status.slice(0, 10).map((d, i) => (
                                    <tr key={i} className="border-t border-white/5">
                                      <td className="px-3 py-2 text-white/90">{d.device_name || "—"}</td>
                                      <td className="px-3 py-2 text-white/70">{d.user || d.user_name || "—"}</td>
                                      <td className="px-3 py-2 text-white/80">
                                        {d.category === "deferred"
                                          ? "Deferred"
                                          : (d.install_state || d.category || "—")}
                                      </td>
                                      <td className="px-3 py-2 text-white/60 text-xs">
                                        {d.last_sync ? formatDate(d.last_sync) : "—"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {deployment.per_device_status.length > 10 && (
                                <button
                                  type="button"
                                  className="w-full py-2 text-xs text-cyan-300 hover:bg-white/5"
                                  onClick={() => setDrillDownId(deployment.id)}
                                  data-testid={`view-all-devices-${deployment.id}`}
                                >
                                  View all {deployment.per_device_status.length} devices →
                                </button>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-white/50">
                              No per-device data cached yet.{" "}
                              <button
                                type="button"
                                className="underline hover:text-cyan-300"
                                onClick={() => setDrillDownId(deployment.id)}
                                data-testid={`open-drill-down-${deployment.id}`}
                              >
                                Open full drill-down
                              </button>
                            </p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        </>
        )}

        {activeTab === "health" && (
          <HealthDashboard 
            data={healthData} 
            loading={healthLoading} 
            onRefresh={fetchHealth} 
          />
        )}
      </main>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight">
              Deployment Details
            </DialogTitle>
            <DialogDescription>
              {selectedDeployment?.app_name} - {selectedDeployment?.version}
            </DialogDescription>
          </DialogHeader>

          {selectedDeployment && (
            <div className="space-y-6 py-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-5 gap-4">
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-4">
                  <div className="text-2xl font-bold text-center text-white">{selectedDeployment.device_stats?.total || 0}</div>
                  <div className="text-xs text-center text-white/90">Total Devices</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-4">
                  <div className="text-2xl font-bold text-center text-green-400">{selectedDeployment.device_stats?.installed || 0}</div>
                  <div className="text-xs text-center text-white/90">Installed</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-4">
                  <div className="text-2xl font-bold text-center text-yellow-400">{selectedDeployment.device_stats?.pending || 0}</div>
                  <div className="text-xs text-center text-white/90">Pending</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-4">
                  <div className="text-2xl font-bold text-center text-sky-400">{selectedDeployment.device_stats?.deferred || 0}</div>
                  <div className="text-xs text-center text-white/90">Deferred</div>
                </div>
                <div className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg p-4">
                  <div className="text-2xl font-bold text-center text-red-400">{selectedDeployment.device_stats?.failed || 0}</div>
                  <div className="text-xs text-center text-white/90">Failed</div>
                </div>
              </div>

              {/* Device Breakdown Summary */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">Device Status Breakdown</h4>
                  {getStatusSourceBadge(selectedDeployment.status_source, selectedDeployment.cache_age_seconds, selectedDeployment.status_error)}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 border border-green-500/30 bg-green-500/10 rounded-md">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="font-medium text-green-300">Installed Successfully</span>
                    </div>
                    <span className="font-bold text-green-400">{selectedDeployment.device_stats?.installed || 0} devices</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border border-yellow-500/30 bg-yellow-500/10 rounded-md">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-yellow-400" />
                      <span className="font-medium text-yellow-300">Pending / Not Received</span>
                    </div>
                    <span className="font-bold text-yellow-400">{selectedDeployment.device_stats?.pending || 0} devices</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border border-sky-500/30 bg-sky-500/10 rounded-md">
                    <div className="flex items-center gap-2">
                      <PauseCircle className="h-4 w-4 text-sky-400" />
                      <span className="font-medium text-sky-300" title="Win32 1602 / 0x80070642 — the user chose Defer. Intune retries on the next device sync.">
                        Deferred by User
                      </span>
                    </div>
                    <span className="font-bold text-sky-400">{selectedDeployment.device_stats?.deferred || 0} devices</span>
                  </div>

                  <div className="flex items-center justify-between p-3 border border-red-500/30 bg-red-500/10 rounded-md">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-400" />
                      <span className="font-medium text-red-300">Installation Failed</span>
                    </div>
                    <span className="font-bold text-red-400">{selectedDeployment.device_stats?.failed || 0} devices</span>
                  </div>
                </div>
              </div>

              {/* Per-Device Status for Test Deployments */}
              {selectedDeployment.deployment_stage === "test" && selectedDeployment.per_device_status?.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">Per-User Installation Status</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRefreshStatus(selectedDeployment.id)}
                      disabled={refreshingStatus[selectedDeployment.id]}
                      className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${refreshingStatus[selectedDeployment.id] ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                  
                  <ScrollArea className="h-[200px] border border-[rgba(229,239,250,0.1)] rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Device</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Last Sync</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDeployment.per_device_status.map((device, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium text-sm text-white">
                              {device.user || "Unknown"}
                            </TableCell>
                            <TableCell className="text-sm text-white/90">
                              {device.device_name || "Unknown"}
                            </TableCell>
                            <TableCell>
                              {device.category === "installed" && (
                                <Badge className="bg-green-600 text-white border-green-700">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Installed
                                </Badge>
                              )}
                              {device.category === "pending" && (
                                <Badge className="bg-yellow-500 text-white border-yellow-600">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {device.install_state || "Pending"}
                                </Badge>
                              )}
                              {device.category === "deferred" && (
                                <Badge
                                  className="bg-sky-500 text-white border-sky-600"
                                  title="Win32 1602 / 0x80070642 — the user chose Defer. Intune retries on the next device sync."
                                >
                                  <PauseCircle className="h-3 w-3 mr-1" />
                                  Deferred
                                </Badge>
                              )}
                              {device.category === "failed" && (
                                <Badge className="bg-red-600 text-white border-red-700">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Failed
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-white/90">
                              {device.last_sync ? new Date(device.last_sync).toLocaleString() : "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}

              {/* No data warning for test deployments */}
              {selectedDeployment.deployment_stage === "test" && 
               selectedDeployment.status_source === "placeholder" && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 rounded-md">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-300">No real-time data available</p>
                      <p className="text-sm text-yellow-400/80 mt-1">
                        Click the refresh button to fetch live installation status from Intune. 
                        Make sure you are logged in with an account that has Intune permissions.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Deployment Info */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-[rgba(229,239,250,0.1)]">
                  <span className="text-white/90">Intune App ID</span>
                  <span className="font-mono text-white">{selectedDeployment.intune_app_id}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[rgba(229,239,250,0.1)]">
                  <span className="text-white/90">Deployed At</span>
                  <span className="text-white">{formatDate(selectedDeployment.deployed_at)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[rgba(229,239,250,0.1)]">
                  <span className="text-white/90">Target Group</span>
                  <span className="text-white">{selectedDeployment.target_group || "All Devices"}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[rgba(229,239,250,0.1)]">
                  <span className="text-white/90">Deployment Stage</span>
                  {getStageBadge(selectedDeployment)}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)} className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Test Members Modal */}
      <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight flex items-center gap-2">
              <Users className="h-5 w-5" />
              Manage Test Members
            </DialogTitle>
            <DialogDescription>
              Update test group members for {editingDeployment?.app_name}. These users will receive the test deployment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm rounded-md">
              <p className="text-blue-300">
                <strong>Note:</strong> Changes will update both the local database and the Azure AD group (if authenticated). Maximum 5 members allowed.
              </p>
            </div>
            
            <div className="space-y-3">
              <Label className="text-sm font-medium text-white">Test Group Members (max 5)</Label>
              {editMembers.map((member, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder={`User email ${index + 1}`}
                    value={member}
                    onChange={(e) => updateMember(index, e.target.value)}
                    className="flex-1 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white placeholder:text-white/70"
                    data-testid={`edit-member-${index}`}
                  />
                  {editMembers.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMember(index)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              {editMembers.length < 5 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addMemberInput}
                  className="w-full bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Member
                </Button>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembersModal(false)} disabled={savingMembers} className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
              Cancel
            </Button>
            <Button
              onClick={handleSaveMembers}
              disabled={savingMembers}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {savingMembers ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save Members
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Go LIVE Modal. Before a rollout exists it shows the deployment
          pipeline the family's next rollout will use (resolved by the
          backend); once one exists it renders every stage from the
          phased deployment document. Stages after UAT are either a
          percentage sample or a named Entra group, 1-10 of them. */}
      <Dialog open={showPhasedDeployModal} onOpenChange={setShowPhasedDeployModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight flex items-center gap-2">
              <Rocket className="h-5 w-5 text-green-400" />
              Go LIVE - Phased Deployment
            </DialogTitle>
            <DialogDescription>
              {phasedDeployTarget?.app_name} v{phasedDeployTarget?.version}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Show different content based on deployment status */}
            {phaseStatuses.phases?.length > 0 ? (
              <>
                {/* Active Deployment Status */}
                <div className="p-4 bg-slate-800 rounded-lg text-sm border border-slate-700">
                  <div className="font-semibold text-white mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-400" />
                    Phased Deployment Active
                  </div>
                  {phaseStatuses.pipeline?.template_name && (
                    <div className="text-slate-300 mb-2" data-testid="go-live-active-pipeline-name">
                      Pipeline: <strong className="text-cyan-400">{phaseStatuses.pipeline.template_name}</strong>
                    </div>
                  )}
                  <div className="text-slate-300 mb-2">
                    Total devices: <strong className="text-cyan-400">{phaseStatuses.total_devices ?? 'N/A'}</strong>
                  </div>
                  <div className="text-slate-300">
                    Current status: <strong className="text-cyan-400">{phaseStatuses.status || 'Unknown'}</strong>
                  </div>
                </div>

                {/* Stage Progress Cards: one per stage after UAT */}
                <div className="space-y-3" data-testid="go-live-phase-list">
                  <div className="text-sm font-semibold text-slate-200">Stage Progress</div>
                  {phaseStatuses.phases.map((phase) => {
                    const isGroup = isNamedGroupPhase(phase);
                    const isActive = phase.status === "active" || phase.status === "completed";
                    const isPending = !phase.status || phase.status === "pending";
                    const isFailed = phase.status === "failed";

                    return (
                      <div
                        key={phase.phase}
                        data-testid={`go-live-phase-${phase.phase}`}
                        className={`p-3 rounded-lg border ${
                          isActive ? "bg-green-900/30 border-green-700" :
                          isFailed ? "bg-red-900/30 border-red-700" :
                          isPending ? "bg-slate-800/50 border-slate-700" :
                          "bg-slate-800 border-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${
                              isActive ? "text-green-400" :
                              isFailed ? "text-red-400" :
                              "text-slate-400"
                            }`}>
                              {phase.label}
                            </span>
                            {phase.status === "completed" && (
                              <CheckCircle2 className="h-4 w-4 text-green-400" />
                            )}
                            {phase.status === "active" && (
                              <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">In Progress</span>
                            )}
                            {isFailed && (
                              <XCircle className="h-4 w-4 text-red-400" />
                            )}
                          </div>
                          {isPending ? (
                            <span className="text-xs text-slate-500">Not started</span>
                          ) : (
                            <span className={`text-xs px-2 py-1 rounded font-medium ${
                              phase.status === "completed" ? "bg-green-600 text-white" :
                              phase.status === "active" ? "bg-blue-600 text-white" :
                              phase.status === "failed" ? "bg-red-600 text-white" :
                              "bg-slate-600 text-slate-200"
                            }`}>
                              {isGroup
                                ? (phase.device_count != null ? `${phase.device_count} devices` : "Group assigned")
                                : `${phase.devices_added || 0}/${phase.device_count || 0} devices`}
                            </span>
                          )}
                        </div>
                        {phase.group_name && (
                          <div className="text-xs text-slate-400 mt-1 truncate" title={phase.group_name}>
                            {isGroup ? "Entra group (as-is): " : ""}
                            <span className="font-mono">{phase.group_name}</span>
                          </div>
                        )}
                        {phase.error && (
                          <div className="text-xs text-red-400 mt-1">
                            Error: {phase.error}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Assignment Info */}
                <div className="p-3 bg-green-900/30 rounded-lg text-sm border border-green-700/50">
                  <div className="font-semibold text-green-400 mb-1">Deployment Settings:</div>
                  <ul className="text-green-300 space-y-1 text-xs">
                    <li>• Assignment type: <strong>Required (Mandatory)</strong></li>
                    <li>• Toast notifications: <strong>Disabled</strong></li>
                    {phaseStatuses.phases.some(isNamedGroupPhase) && (
                      <li>• Named Entra groups are assigned as-is and stay assigned as later stages roll out</li>
                    )}
                  </ul>
                </div>
              </>
            ) : (
              <>
                {/* No deployment yet - show the pipeline this rollout will use */}
                <div className="p-4 bg-slate-800 rounded-lg text-sm border border-slate-700" data-testid="go-live-pipeline-preview">
                  <div className="font-semibold text-white mb-2 flex items-center gap-2">
                    <Rocket className="h-4 w-4 text-cyan-400" />
                    Start Phased Deployment
                  </div>
                  {resolvedPipeline.loading ? (
                    <div className="flex items-center gap-2 text-slate-300" data-testid="go-live-pipeline-loading">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading the deployment pipeline…
                    </div>
                  ) : resolvedPipeline.error || resolvedPostUatStages.length === 0 ? (
                    <p className="text-amber-300 text-xs" data-testid="go-live-pipeline-error">
                      {resolvedPipeline.error || "No stages were returned for this app's deployment pipeline."}{" "}
                      The rollout will use the pipeline configured for this app when it starts.
                    </p>
                  ) : (
                    <>
                      {resolvedPipeline.data?.template_name && (
                        <p className="text-slate-300 mb-2" data-testid="go-live-pipeline-name">
                          Pipeline: <strong className="text-cyan-400">{resolvedPipeline.data.template_name}</strong>
                        </p>
                      )}
                      {/* The whole chain, Pre-UAT and UAT included */}
                      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="go-live-pipeline-chain">
                        {resolvedStages.map((s, i) => (
                          <React.Fragment key={s.id || `${s.type}-${i}`}>
                            {i > 0 && <ChevronRight className="h-3 w-3 text-slate-500" />}
                            <span
                              className={`text-xs px-2 py-0.5 rounded border ${
                                s.type === "pre_uat" || s.type === "uat"
                                  ? "border-slate-600 text-slate-400"
                                  : s.type === "group"
                                    ? "border-sky-600 text-sky-300"
                                    : "border-cyan-600 text-cyan-300"
                              }`}
                            >
                              {resolvedStageChipLabel(s)}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                      <ul className="text-slate-300 space-y-1 text-xs">
                        {resolvedPostUatStages.map((s, i) => (
                          <li key={s.id || i} className="flex items-start gap-2" data-testid={`go-live-pipeline-stage-${i + 1}`}>
                            <span className="text-cyan-400 font-medium shrink-0">{resolvedStageLabel(s, i)}:</span>
                            <span>
                              {s.type === "percentage"
                                ? `${s.percentage}% of devices`
                                : <>Entra group <span className="font-mono text-sky-300">{s.group_name || "—"}</span>, assigned as-is</>}
                              {s.threshold != null && (
                                <span className="text-slate-500"> · success threshold {s.threshold}%</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                      {resolvedPostUatStages.some((s) => s.type === "percentage") && (
                        <p className="text-slate-400 text-xs mt-3">
                          Devices with previous versions of <strong className="text-cyan-400">{phasedDeployTarget?.app_name}</strong> are detected automatically and sampled into the percentage stages.
                        </p>
                      )}
                      {resolvedPostUatStages.some((s) => s.type === "group") && (
                        <p className="text-slate-400 text-xs mt-2" data-testid="go-live-pipeline-group-note">
                          Named groups are assigned as they are in Entra: ALICE doesn't change their
                          membership, and each stays assigned as later stages roll out (cumulative).
                        </p>
                      )}
                      {resolvedPipeline.data?.note && (
                        <p className="text-slate-400 text-xs mt-2">{resolvedPipeline.data.note}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Assignment Type Info */}
                <div className="p-3 bg-green-900/30 rounded-lg text-sm border border-green-700/50">
                  <div className="font-semibold text-green-400 mb-1">Deployment Settings:</div>
                  <ul className="text-green-300 space-y-1 text-xs">
                    <li>• Assignment type: <strong>Required (Mandatory)</strong></li>
                    <li>• Toast notifications: <strong>Disabled</strong></li>
                    {resolvedPostUatStages.some((s) => s.type === "percentage") && (
                      <li>• Groups for percentage stages are created automatically in Entra ID</li>
                    )}
                    {resolvedPostUatStages.some((s) => s.type === "group") && (
                      <li>• Named stages use your existing Entra ID groups</li>
                    )}
                  </ul>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPhasedDeployModal(false)} disabled={deployingPhase || rollingBack} className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
              Close
            </Button>

            {/* Show appropriate action button based on current state */}
            {phaseStatuses.phases?.length > 0 ? (
              // Deployment exists - show Rollback (at any stage) and
              // Proceed (while a stage is left and it isn't complete).
              <>
                {phaseStatuses.current_phase >= 1 && phaseStatuses.current_phase <= phaseStatuses.phases.length && (
                  <Button
                    onClick={() => setShowRollbackConfirm(true)}
                    disabled={deployingPhase || rollingBack}
                    variant="outline"
                    data-testid="rollback-phase-btn"
                    className="border-amber-500 text-amber-500 hover:bg-amber-500/10 hover:text-amber-400"
                  >
                    {rollingBack ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Rolling back...
                      </>
                    ) : (
                      <>
                        <Undo2 className="h-4 w-4 mr-2" />
                        Rollback to {rollbackTargetName}
                      </>
                    )}
                  </Button>
                )}
                {(phaseStatuses.current_phase || 0) < phaseStatuses.phases.length &&
                 !isLiveCompleted(phaseStatuses.status) && (
                  <Button
                    onClick={proceedToNextPhase}
                    disabled={deployingPhase || rollingBack}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="proceed-phase-btn"
                  >
                    {deployingPhase ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deploying...
                      </>
                    ) : (
                      <>
                        <Rocket className="h-4 w-4 mr-2" />
                        Proceed to {nextPhaseEntry?.label || "next stage"}
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : (
              // No deployment yet - show start button
              <Button
                onClick={executePhasedDeployment}
                disabled={deployingPhase}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="start-go-live-btn"
              >
                {deployingPhase ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <Rocket className="h-4 w-4 mr-2" />
                    {resolvedPostUatStages.length > 0
                      ? `Start ${resolvedStageLabel(resolvedPostUatStages[0], 0)}`
                      : "Start rollout"}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback confirmation dialog. Destructive action -- require an
          explicit confirm click. The action is idempotent per-phase
          (backend refuses to roll back a deployment that is not in a
          rollback-eligible state) so an accidental double-click can't
          skip past the intended target phase. */}
      <Dialog open={showRollbackConfirm} onOpenChange={(o) => { if (!o) setShowRollbackConfirm(false); }}>
        <DialogContent className="bg-slate-950 border-amber-500/40 text-slate-100 max-w-lg" data-testid="rollback-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              Rollback deployment?
            </DialogTitle>
            <DialogDescription className="text-slate-300 pt-2">
              This will reassign the Intune app to the previous target and
              remove the current stage&apos;s assignment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-sm">
            <div className="rounded-md bg-slate-900/70 border border-slate-800 p-3 space-y-1">
              <div>
                <span className="text-slate-400">App: </span>
                <strong className="text-cyan-300">
                  {phasedDeployTarget?.app_name}
                </strong>
                {phasedDeployTarget?.version && (
                  <span className="text-slate-400"> v{phasedDeployTarget.version}</span>
                )}
              </div>
              <div>
                <span className="text-slate-400">From: </span>
                <strong className="text-slate-100" data-testid="rollback-from-stage">
                  {currentPhaseEntry?.label || `Phase ${currentPhaseNum}`}
                </strong>
              </div>
              <div>
                <span className="text-slate-400">To: </span>
                <strong className="text-amber-300" data-testid="rollback-to-stage">
                  {currentPhaseNum <= 1
                    ? "Test group (intent: Available)"
                    : rollbackTargetName}
                </strong>
              </div>
            </div>

            <ul className="text-xs text-slate-400 space-y-1 pl-4 list-disc">
              <li>The current stage&apos;s group assignment will be removed from Intune.</li>
              <li>
                The previous stage (or test) group will be reassigned.
                Entra group membership is left untouched.
              </li>
              <li>Exclusion group assignments are preserved.</li>
              <li>You can roll back one step at a time, and progress again afterwards.</li>
            </ul>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRollbackConfirm(false)}
              disabled={rollingBack}
              data-testid="rollback-cancel-btn"
              className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={rollbackPhase}
              disabled={rollingBack}
              data-testid="rollback-confirm-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {rollingBack ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rolling back...
                </>
              ) : (
                <>
                  <Undo2 className="h-4 w-4 mr-2" />
                  Confirm rollback to {rollbackTargetName}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proceed-to-next-phase confirmation dialog (row-level action). */}
      <Dialog open={showProceedConfirm} onOpenChange={(o) => { if (!o) setShowProceedConfirm(false); }}>
        <DialogContent className="bg-slate-950 border-blue-500/40 text-slate-100 max-w-lg" data-testid="proceed-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-400">
              <Rocket className="h-5 w-5" />
              Proceed to {nextPhaseEntry?.name || "the next stage"}?
            </DialogTitle>
            <DialogDescription className="text-slate-300 pt-2">
              This will expand the rollout by assigning the Intune app to the
              next stage&apos;s group.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-sm">
            <div className="rounded-md bg-slate-900/70 border border-slate-800 p-3 space-y-1">
              <div>
                <span className="text-slate-400">App: </span>
                <strong className="text-cyan-300">
                  {phasedDeployTarget?.app_name}
                </strong>
                {phasedDeployTarget?.version && (
                  <span className="text-slate-400"> v{phasedDeployTarget.version}</span>
                )}
              </div>
              <div>
                <span className="text-slate-400">From: </span>
                <strong className="text-slate-100" data-testid="proceed-from-stage">
                  {currentPhaseEntry?.label || `Phase ${currentPhaseNum}`}
                </strong>
              </div>
              <div>
                <span className="text-slate-400">To: </span>
                <strong className="text-blue-300" data-testid="proceed-to-stage">
                  {nextPhaseEntry?.label || `Phase ${currentPhaseNum + 1}`}
                  {nextPhaseEntry && isNamedGroupPhase(nextPhaseEntry) && nextPhaseEntry.group_name && (
                    <span className="text-slate-400 font-normal"> (Entra group <span className="font-mono">{nextPhaseEntry.group_name}</span>, as-is)</span>
                  )}
                </strong>
              </div>
            </div>

            <ul className="text-xs text-slate-400 space-y-1 pl-4 list-disc">
              <li>The next stage&apos;s group will be assigned in Intune with intent "required".</li>
              <li>Named Entra groups from earlier stages stay assigned.</li>
              <li>You can roll back one stage at a time if issues appear.</li>
            </ul>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowProceedConfirm(false)}
              disabled={deployingPhase}
              data-testid="proceed-cancel-btn"
              className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmProceedPhase}
              disabled={deployingPhase}
              data-testid="proceed-confirm-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {deployingPhase ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deploying...
                </>
              ) : (
                <>
                  <Rocket className="h-4 w-4 mr-2" />
                  Confirm {nextPhaseEntry?.name || "next stage"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-UAT -> UAT confirmation (row-level action). */}
      <Dialog open={!!promoteTarget} onOpenChange={(o) => { if (!o && !promoting) setPromoteTarget(null); }}>
        <DialogContent className="max-w-lg" data-testid="promote-uat-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Move to UAT?
            </DialogTitle>
            <DialogDescription className="pt-2">
              {promoteTarget?.app_name}{promoteTarget?.version ? ` v${promoteTarget.version}` : ""} leaves{" "}
              {promoteTarget?.pre_uat_stage_name || "Pre-UAT"} and goes to user acceptance testing.
            </DialogDescription>
          </DialogHeader>

          <ul className="text-xs space-y-1 pl-4 list-disc py-2">
            <li>A test group is created with the testers recorded for this package, and the app is assigned to it (Available).</li>
            <li>
              The {promoteTarget?.pre_uat_stage_name || "Pre-UAT"} group
              {promoteTarget?.pre_uat_group_name ? <> (<span className="font-mono">{promoteTarget.pre_uat_group_name}</span>)</> : null}
              {" "}keeps its assignment.
            </li>
            <li>Testers are emailed to approve or reject the package.</li>
          </ul>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPromoteTarget(null)}
              disabled={promoting}
              data-testid="promote-uat-cancel-btn"
              className="bg-[#E5EFFA] text-[#220175] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmPromoteToUat}
              disabled={promoting}
              data-testid="promote-uat-confirm-btn"
              className="bg-sky-600 hover:bg-sky-700 text-white"
            >
              {promoting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Moving...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  Confirm move to UAT
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeploymentDrillDown
        open={!!drillDownId}
        onOpenChange={(o) => { if (!o) setDrillDownId(null); }}
        packageId={drillDownId}
      />
    </div>
  );
}
